/**
 * 고객사 ID 관련 유틸리티 함수
 * 
 * 고객사 ID는 세 가지 상태를 가질 수 있습니다:
 * - undefined: "전체" 고객사 (필터링 없음)
 * - null: "(미입력)" 고객사 (customer_id가 null인 경우)
 * - number: 특정 고객사 ID
 * 
 * URL 파라미터에서는 문자열로 저장되므로 변환이 필요합니다:
 * - 'all': undefined로 변환
 * - 'none': null로 변환
 * - 숫자 문자열: number로 변환
 */

export type CustomerIdFilter = number | null | undefined;
export type CustomerIdString = string;

/**
 * 문자열 형태의 고객사 ID를 실제 타입으로 변환
 * 
 * @param idStr - URL 파라미터에서 가져온 문자열 ('all', 'none', 또는 숫자)
 * @returns 변환된 고객사 ID (undefined, null, 또는 number)
 * 
 * @example
 * parseCustomerId('all') // undefined (전체)
 * parseCustomerId('none') // null (미입력)
 * parseCustomerId('123') // 123 (특정 고객사)
 */
export const parseCustomerId = (idStr: string): CustomerIdFilter => {
  if (idStr === 'none') return null;
  if (idStr === 'all' || idStr === '') return undefined;
  
  const parsed = parseInt(idStr, 10);
  return isNaN(parsed) ? undefined : parsed;
};

/**
 * 고객사 ID를 문자열 형태로 변환
 * 
 * @param id - 고객사 ID (undefined, null, 또는 number)
 * @returns URL 파라미터용 문자열 ('all', 'none', 또는 숫자 문자열)
 * 
 * @example
 * stringifyCustomerId(undefined) // 'all'
 * stringifyCustomerId(null) // 'none'
 * stringifyCustomerId(123) // '123'
 */
export const stringifyCustomerId = (id: CustomerIdFilter): CustomerIdString => {
  if (id === null) return 'none';
  if (id === undefined) return 'all';
  return String(id);
};

/**
 * Select 컴포넌트의 value 속성에 사용할 문자열 반환
 * parseCustomerId와 동일하지만 명확한 의도를 나타냄
 * 
 * @param id - 고객사 ID
 * @returns Select value 문자열
 */
export const getCustomerSelectValue = (id: CustomerIdFilter): string => {
  return stringifyCustomerId(id);
};

/**
 * API 호출 시 사용할 고객사 ID 변환
 * null은 API에 따라 0 또는 undefined로 변환될 수 있음
 * 
 * @param id - 고객사 ID
 * @param nullAsZero - null을 0으로 변환할지 여부 (기본: false)
 * @returns API용 고객사 ID
 */
export const getCustomerIdForAPI = (
  id: CustomerIdFilter,
  nullAsZero: boolean = false
): number | undefined => {
  if (id === null) return nullAsZero ? 0 : undefined;
  if (id === undefined) return undefined;
  return id;
};

/**
 * 고객사 필터 레이블 반환
 * 
 * @param id - 고객사 ID
 * @param customerName - 고객사명 (number일 때)
 * @returns 표시용 레이블
 */
export const getCustomerFilterLabel = (
  id: CustomerIdFilter,
  customerName?: string
): string => {
  if (id === null) return '(미입력)';
  if (id === undefined) return '전체';
  return customerName || `고객사 ${id}`;
};

